import { showError } from '@components/app-error';
import { FormGroup } from '@components/async-form';
import { IcoCheck } from '@components/icons';
import { ModalForm, showModalForm } from '@components/modal-form';
import { useCurrentTenant } from '@components/router/session-context';
import { rpx } from 'client/lib/rpx-client';
import { useState } from 'preact/hooks';
import {
  checkNextCohortStart,
  nextCohortTitle,
  nextMondayAfter,
} from 'shared/immersion-sprint-setup';
import { fmtTrackerDate, isIsoDate } from 'shared/tracker-people';
import { URLS } from 'shared/urls';
import { AcesProgram } from 'shared/aces-program';
import { CopyPricingToggle } from './copy-course-modal';

type Props = {
  course: { id: UUID; title: string; acesProgram?: AcesProgram };
  /** The source's standup start date. Without it the copy can't tell how far to move dates. */
  startDate?: string;
  /** The source's standup end date: the default start is the Monday after it. */
  endDate?: string;
  /** The course's today, for the date style. */
  today: string;
  /** Only the guide can copy prices. */
  canCopyPricing: boolean;
};

const CARRIES = [
  'Standup days, questions, reminders and dates',
  'Weekly summary and notify address',
  'Deadline: lessons, emails and last removal day',
  'Tracker settings',
];

export function showNextCohortModal(props: Props) {
  showModalForm(({ resolve }) => {
    return <NextCohortModal {...props} onCancel={() => resolve(undefined)} />;
  });
}

function StartDateError({
  newStart,
  check,
  today,
  onPick,
}: {
  newStart: string;
  check: ReturnType<typeof checkNextCohortStart>;
  today: string;
  onPick(date: string): void;
}) {
  if (check.ok) {
    return;
  }
  const fmt = (date: string) => fmtTrackerDate(date, today);
  let message;
  if (check.reason === 'not-monday') {
    const [before, after] = check.choices;
    message = (
      <>
        {fmt(newStart)} isn't a Monday. Immersion sprints start on a Monday. Pick{' '}
        <button type="button" class="underline font-medium" onClick={() => onPick(before)}>
          {fmt(before)}
        </button>{' '}
        or{' '}
        <button type="button" class="underline font-medium" onClick={() => onPick(after)}>
          {fmt(after)}
        </button>
        .
      </>
    );
  } else if (check.reason === 'not-after') {
    message = 'Pick a start date after this course starts.';
  } else {
    message = 'Pick a start date within two years of this course’s start.';
  }
  return (
    <p role="alert" class="text-sm text-red-700">
      {message}
    </p>
  );
}

export function NextCohortModal({
  course,
  startDate,
  endDate,
  today,
  canCopyPricing,
  onCancel,
}: Props & { onCancel(): void }) {
  const { terminology, isCore } = useCurrentTenant();
  const [newStart, setNewStart] = useState(endDate ? nextMondayAfter(endDate) : '');
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [shouldCopyPricing, setShouldCopyPricing] = useState(false);

  const currentTitle = title ?? nextCohortTitle(course.title, newStart || undefined);
  const check =
    startDate && isIsoDate(newStart) ? checkNextCohortStart(startDate, newStart) : undefined;
  const weeks = check?.ok ? check.weeks : undefined;
  const fmt = (date: string) => fmtTrackerDate(date, today);

  async function copyCourse() {
    if (!weeks || !currentTitle.trim()) {
      return;
    }
    try {
      const { newCourseId } = await rpx.courses.copyCourseAsGuide({
        id: course.id,
        title: currentTitle.trim(),
        shouldCopyPricing,
        shouldDraftContent: false,
        nextCohortWeeks: weeks,
      });
      // A hard redirect, as the ordinary copy does, so no screen keeps the old course id.
      location.assign(
        URLS.guide.coursePage({ courseId: newCourseId, page: 'settings/immersion-sprint' }),
      );
    } catch (err) {
      showError(err);
    }
  }

  return (
    <ModalForm
      onClose={onCancel}
      title="Copy as next cohort"
      subtitle={
        <>
          You're copying: <i>{course.title}</i>
        </>
      }
      onSubmit={copyCourse}
      confirmButtonText="Copy course"
      isConfirmDisabled={!weeks || !currentTitle.trim()}
    >
      <section class="flex flex-col gap-6">
        <FormGroup prop="title">
          <label class="flex flex-col gap-1">
            <span>New course name</span>
            <input
              type="text"
              name="title"
              class="ruz-input"
              value={currentTitle}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
            />
          </label>
        </FormGroup>

        {startDate ? (
          <FormGroup prop="startDate">
            <label class="flex flex-col gap-1">
              <span>Start date</span>
              <input
                type="date"
                name="startDate"
                class="ruz-input"
                value={newStart}
                aria-describedby="next-cohort-start-help"
                onInput={(e) => setNewStart((e.target as HTMLInputElement).value)}
              />
            </label>
            <p id="next-cohort-start-help" class="text-sm text-gray-500">
              A Monday
            </p>
            {check && (
              <StartDateError
                newStart={newStart}
                check={check}
                today={today}
                onPick={setNewStart}
              />
            )}
          </FormGroup>
        ) : (
          <p role="alert" class="text-sm text-amber-800">
            Set this course's standup start date first, so the copy knows how far to move the dates.{' '}
            <a
              class="font-medium underline"
              href={URLS.guide.coursePage({ courseId: course.id, page: 'settings/checkins' })}
            >
              Set start date
            </a>
          </p>
        )}

        {check?.ok && (
          <p class="rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Moves every date {check.weeks} {check.weeks === 1 ? 'week' : 'weeks'} later.{' '}
            {check.standupStart === check.weekOneStart
              ? `Week 1 starts ${fmt(check.weekOneStart)}.`
              : `Standups start ${fmt(check.standupStart)}, as this course's start isn't a Monday.`}
          </p>
        )}

        <div class="flex flex-col gap-2">
          <span class="font-semibold">Carries</span>
          <ul class="flex flex-col gap-1 text-sm">
            {[
              weeks
                ? `Lessons, messages and calls, ${weeks} ${weeks === 1 ? 'week' : 'weeks'} later`
                : 'Lessons, messages and calls',
              ...CARRIES,
              ...(isCore && course.acesProgram ? ['Show in ACES'] : []),
            ].map((text) => (
              <li key={text} class="flex items-start gap-2">
                <IcoCheck class="w-4 h-4 mt-0.5 shrink-0 text-green-700" />
                {text}
              </li>
            ))}
          </ul>
          <span class="text-sm text-gray-600">
            Automatic removal starts off. Turn it on in Completion gate when the cohort is ready.
          </span>
        </div>

        {canCopyPricing && (
          <CopyPricingToggle
            checked={shouldCopyPricing}
            onToggle={() => setShouldCopyPricing(!shouldCopyPricing)}
            courseLabel={terminology.course}
          />
        )}
      </section>
    </ModalForm>
  );
}
